import { useTranslation } from 'next-i18next';
import { FC, useCallback } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import useTimeout from 'src/common/hooks/useTimeout';
import { useSearchInputContext, type SearchRefs } from 'src/common/components/Search/SearchInputProvider';
import CapacityFilter from 'src/common/components/CapacityFilter';
import Typography from 'src/common/components/Typography';
import { useSearchHeroModalContext } from 'src/common/components/Search/SearchHero/SearchHeroModalProvider';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import styles from './PopoverGuests.module.scss';

type Props = {
  refs: SearchRefs;
};
const PopoverGuests: FC<Props> = ({
  refs,
}) => {
  const [t] = useTranslation('common');
  const {
    filterGuests, setFilterGuests, isDesktop, submitSearch,
  } = useSearchInputContext();
  const { close, isGuestsOpen } = useSearchHeroModalContext();

  const onBlurAfterRender = useAfterRenderCallback(() => {
    if (refs.inputs?.current?.contains(document.activeElement)) return;

    close();
  });

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
      close();
    }

    // Caters for both directions.
    if (e.key === 'Tab') {
      onBlurAfterRender();
    }
  }, [close, onBlurAfterRender, submitSearch]);

  const isOpen = isGuestsOpen;
  const disabled = !isDesktop || !isOpen;

  const closeOutsideRef = useOnclickOutside(() => {
    close();
  }, { disabled, refs: [refs?.inputs] });

  useTimeout(() => {
    refs.who.current?.setSelectionRange(0, 0);
    refs.capacityNumber.current?.focus();
  }, 0, !disabled);

  if (disabled) return null;

  return (
    <div
      ref={closeOutsideRef}
      className={styles.root}
    >
      <Typography
        type="h3"
        size="body-xl"
        weight="semibold"
      >
        {t('search--search-who-title')}
      </Typography>
      <CapacityFilter
        inputNumRef={refs.capacityNumber}
        value={filterGuests}
        onChange={setFilterGuests}
        onKeyDown={onKeyDown}
        classNameModifier="guestFilter"
        buttonsTabIndex={-1}
      />
    </div>
  );
};

export default PopoverGuests;
